import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

/**
 * LocationContext — détection de la ville visiteur :
 *
 * 1) GPS navigateur (navigator.geolocation) + reverse geocoding Nominatim
 *    → déclenché AUTOMATIQUEMENT au chargement (prompt navigateur immédiat,
 *      comportement type "tag Google"). Donne la ville exacte du visiteur.
 * 2) Backend `/api/geo` (IP via proxy → ip-api.com / ipapi.co / ipwho.is)
 * 3) Fournisseurs IP côté client (CORS direct)
 *
 * GPS et IP démarrent en parallèle : la première source GPS valide remplace
 * toute valeur IP. Cache de session pour éviter les re-prompts.
 */
const FALLBACK_TEXT = "près de chez vous";
const STORAGE_KEY = "voltis_geo_v5";

const LocationContext = createContext({
  city: null,
  region: null,
  country: null,
  countryCode: null,
  displayCity: FALLBACK_TEXT,
  loading: true,
  error: null,
  source: null,
  requestPreciseLocation: () => {},
});

export function useLocation() {
  return useContext(LocationContext);
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_PROVIDERS = [
  {
    name: "ipapi.co",
    url: "https://ipapi.co/json/",
    extract: (d) =>
      d && !d.error && d.city
        ? { city: d.city, region: d.region, country: d.country_name, countryCode: d.country_code }
        : null,
  },
  {
    name: "ipwho.is",
    url: "https://ipwho.is/",
    extract: (d) =>
      d && d.success !== false && d.city
        ? { city: d.city, region: d.region, country: d.country, countryCode: d.country_code }
        : null,
  },
  {
    name: "freeipapi",
    url: "https://freeipapi.com/api/json",
    extract: (d) =>
      d && d.cityName
        ? { city: d.cityName, region: d.regionName, country: d.countryName, countryCode: d.countryCode }
        : null,
  },
  {
    name: "geojs",
    url: "https://get.geojs.io/v1/ip/geo.json",
    extract: (d) =>
      d && d.city
        ? { city: d.city, region: d.region, country: d.country, countryCode: d.country_code }
        : null,
  },
];

async function fetchJson(url, signal, headers = {}) {
  try {
    const res = await fetch(url, { signal, headers: { Accept: "application/json", ...headers } });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Reverse geocode lat/lng → ville française la plus proche, via OpenStreetMap Nominatim.
 * Gratuit, sans clé. Renvoie { city, region, country, countryCode } ou null.
 */
async function reverseGeocode(lat, lng, signal) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=12&accept-language=fr`;
  const data = await fetchJson(url, signal);
  if (!data || data.error) return null;
  const a = data.address || {};
  const city =
    a.city ||
    a.town ||
    a.village ||
    a.municipality ||
    a.suburb ||
    a.county ||
    null;
  if (!city) return null;
  return {
    city,
    region: a.state || a.region || null,
    country: a.country || null,
    countryCode: (a.country_code || "").toUpperCase() || null,
  };
}

export function LocationProvider({ children }) {
  const [state, setState] = useState({
    city: null,
    region: null,
    country: null,
    countryCode: null,
    displayCity: FALLBACK_TEXT,
    loading: true,
    error: null,
    source: null,
  });

  // Une fois GPS confirmé, on bloque tout override IP ultérieur
  const gpsLockedRef = useRef(false);

  const persist = useCallback((next) => {
    setState((prev) => {
      // Ne jamais écraser GPS par une source moins précise
      if (gpsLockedRef.current && next.source !== "browser-gps") {
        return prev;
      }
      return {
        ...prev,
        ...next,
        displayCity: next.city || prev.displayCity || FALLBACK_TEXT,
        loading: false,
      };
    });
    try {
      if (next.city) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          city: next.city,
          region: next.region || null,
          country: next.country || null,
          countryCode: next.countryCode || null,
          source: next.source || null,
        }));
      }
    } catch {
      // ignore
    }
  }, []);

  /**
   * Déclenche le prompt GPS navigateur. Si l'utilisateur accepte,
   * on reverse-geocode et on met à jour la ville (override IP-based).
   * Cette fonction est appelée AUTOMATIQUEMENT au mount + exposée pour
   * permettre un re-trigger manuel.
   */
  const requestPreciseLocation = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const result = await reverseGeocode(latitude, longitude);
        if (result && result.city) {
          gpsLockedRef.current = true;
          persist({ ...result, error: null, source: "browser-gps" });
        }
      },
      (err) => {
        // L'utilisateur a refusé / timeout → on garde la valeur IP-based
        // eslint-disable-next-line no-console
        console.warn("[Voltis] GPS denied or failed:", err && err.message);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 300000 }
    );
  }, [persist]);

  useEffect(() => {
    let cancelled = false;

    // 1) Hydrate depuis cache (mais relance GPS si la source n'était PAS GPS,
    //    pour upgrader vers une vraie position précise).
    let cachedSource = null;
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.city) {
          cachedSource = parsed.source || null;
          setState({
            ...parsed,
            displayCity: parsed.city,
            loading: false,
            error: null,
          });
          if (cachedSource === "browser-gps") {
            gpsLockedRef.current = true;
          }
        }
      }
    } catch {
      // ignore
    }

    // 2) DÉCLENCHEMENT AUTOMATIQUE du prompt GPS — sauf si déjà confirmé GPS
    if (!gpsLockedRef.current) {
      // Léger délai pour éviter de bloquer le premier paint
      const t = setTimeout(() => {
        if (!cancelled) requestPreciseLocation();
      }, 300);
      // cleanup en local
      // eslint-disable-next-line no-unused-vars
      const _ = t;
    }

    // 3) Si pas de cache, lance la résolution IP en fallback parallèle
    if (cachedSource) {
      return () => { cancelled = true; };
    }

    const controller = new AbortController();
    const overall = setTimeout(() => controller.abort(), 9000);

    (async () => {
      // 3a) Backend /api/geo
      if (BACKEND_URL) {
        const data = await fetchJson(`${BACKEND_URL}/api/geo`, controller.signal);
        if (!cancelled && data && data.city) {
          clearTimeout(overall);
          persist({
            city: data.city,
            region: data.region || null,
            country: data.country || null,
            countryCode: data.country_code || null,
            error: null,
            source: data.source || "backend",
          });
          return;
        }
      }

      // 3b) Client-side IP providers
      for (const p of CLIENT_PROVIDERS) {
        if (cancelled) return;
        const data = await fetchJson(p.url, controller.signal);
        const result = data ? p.extract(data) : null;
        if (result && result.city) {
          clearTimeout(overall);
          persist({
            city: result.city,
            region: result.region || null,
            country: result.country || null,
            countryCode: result.countryCode || null,
            error: null,
            source: p.name,
          });
          return;
        }
      }

      if (!cancelled) {
        clearTimeout(overall);
        setState((s) => ({ ...s, loading: false, error: "geo_failed", displayCity: s.city || FALLBACK_TEXT }));
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(overall);
      controller.abort();
    };
  }, [persist, requestPreciseLocation]);

  return (
    <LocationContext.Provider value={{ ...state, requestPreciseLocation }}>
      {children}
    </LocationContext.Provider>
  );
}
